import moment from 'moment';
import {FAILURES, FAILURE_KINDS, NEEDS_TECH, SENTENCE} from './copy';

const RUN_KIND = {
  WRITING: 'writing',
  IN_REVIEW: 'review',
  PUBLISHED: 'published',
  NOT_PUBLISHED: 'notPublished',
  FAILED: 'failed',
};

const clock = value => moment(value).format('h:mm a');

// "today at 4:40 pm", "tomorrow at 4:40 am", "Friday at 9:00 am", "26 Sep at 9:00 am".
export const deadline = (value, now = moment()) =>
  moment(value).calendar(now, {
    sameDay: '[today at] h:mm a',
    nextDay: '[tomorrow at] h:mm a',
    nextWeek: 'dddd [at] h:mm a',
    lastDay: '[yesterday at] h:mm a',
    lastWeek: 'D MMM [at] h:mm a',
    sameElse: 'D MMM [at] h:mm a',
  });

// Why a run couldn't publish, and what happens next, in words. The writing
// tool's own reason wins once it reports one.
export const failureSentence = (run, now = moment()) => {
  const reason = FAILURES[run.failureCode]
    ? FAILURES[run.failureCode](run)
    : FAILURE_KINDS[run.failureKind] || FAILURE_KINDS.NO_RESULT;
  if (NEEDS_TECH.includes(run.failureCode)) {
    return `${reason} ${SENTENCE.needsTech}`;
  }
  if (!run.willRetry) return `${reason} ${SENTENCE.gaveUp}`;
  const left = Math.max((run.maxAttempts ?? 3) - (run.attempts ?? 0), 1);
  const next =
    run.nextAttemptAt && moment(run.nextAttemptAt).isAfter(now)
      ? SENTENCE.retryAt(clock(run.nextAttemptAt), left)
      : SENTENCE.retryNext(left);
  return `${reason} ${next}`;
};

export const cardFromRun = (run, now) => {
  const kind = RUN_KIND[run.status] || 'writing';
  const sentence = {
    writing: SENTENCE.writing,
    review: run.requestedBy ? SENTENCE.reviewRequested : SENTENCE.review,
    notPublished: SENTENCE.notPublished,
    failed: kind === 'failed' ? failureSentence(run, now) : null,
  }[kind];
  return {
    key: `run-${run.id}`,
    kind,
    title: run.title,
    outlets: run.outlets || [],
    articles: run.sources || [],
    time: run.publishedAt || run.heldAt || run.lastAttemptAt || run.startedAt,
    sentence,
    run,
  };
};

export const cardFromCluster = (cluster, now) => {
  const outlets = cluster.outlets || [];
  const kind = cluster.ready ? 'ready' : 'waiting';
  return {
    key: `cluster-${cluster.clusterId}`,
    kind,
    title: cluster.primaryTitle,
    outlets,
    articles: cluster.articles || [],
    time: cluster.publishedDate,
    sentence:
      kind === 'ready'
        ? SENTENCE.ready(outlets.length)
        : SENTENCE.waiting(
            outlets[0] || 'one website',
            cluster.expiresAt && deadline(cluster.expiresAt, now),
          ),
    clusterId: cluster.clusterId,
  };
};

// Ready stories first (they're about to be sent), then newest first.
export const buildCards = ({runs, clusters, now}) => {
  const cards = [
    ...runs.map(run => cardFromRun(run, now)),
    ...clusters.map(cluster => cardFromCluster(cluster, now)),
  ];
  return cards.sort((a, b) => {
    if ((a.kind === 'ready') !== (b.kind === 'ready')) {
      return a.kind === 'ready' ? -1 : 1;
    }
    return moment(b.time).valueOf() - moment(a.time).valueOf();
  });
};
